import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useFetchUser } from '@/hooksApi';
import { useUserStore } from '@/stores';

/**
 * Hook to sync user permissions on route changes
 * - Refetches user data in background on navigation
 * - Updates store only if permissions changed
 * - Debounced to avoid duplicate calls
 */
const usePermissionSync = () => {
  const location = useLocation();
  const { user, setUser } = useUserStore();
  const { refetch } = useFetchUser(false); // Disabled - only need refetch function, not auto-fetch
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousPermissionsRef = useRef<string[] | null | undefined>(user?.permissions);

  // Handle route change refetch
  useEffect(() => {
    // Skip refetch on route change if no user (not authenticated yet)
    if (!user) return;

    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the permission check (300ms)
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await refetch();

        if (data?.object) {
          const newPermissions = data.object.permissions;
          const currentPermissions = previousPermissionsRef.current;

          // Check if permissions actually changed
          const permissionsChanged = !arePermissionsEqual(currentPermissions, newPermissions);

          if (permissionsChanged) {
            // Update store with new user data (including permissions)
            setUser(data.object);
            previousPermissionsRef.current = newPermissions;
          }
        }
      } catch (error) {
        // Error handling is already done by React Query
        // Just log for debugging
        console.error('[PermissionSync] Failed to refetch user:', error);
      }
    }, 300);

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [location.pathname, user, refetch, setUser]);
};

/**
 * Helper to compare two permission arrays
 */
const arePermissionsEqual = (
  a: string[] | null | undefined,
  b: string[] | null | undefined,
): boolean => {
  // Handle null/undefined cases
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;

  // Sort and compare
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();

  return sortedA.every((perm, index) => perm === sortedB[index]);
};

export default usePermissionSync;
